/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState } from 'react';
import { Box, Card, TextField, ToggleButton, ToggleButtonGroup, Button, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { grey } from '@mui/material/colors';
import CanvasApi from '../../services/CanvasApi';
import { GenerateImageResponse } from '../../types/CanvaGenerateInterface';
import Deleteicon from '../../assets/icons/canvasIcons/deleteicon.svg';
import CloudUploadIcon from '../../assets/icons/canvasIcons/cloud_upload.svg';
import { useSnackbar } from '../../components/context/SnackbarContext';

const ReplaceBG = ({
  canvasApi,
  id,
  initialPrompt,
  subjectImage,
  onAction,
  setIsGenerating,
  imageCategories,
  setImageCategories,
  canvasWidth
}: {
  canvasApi: CanvasApi;
  id: string;
  initialPrompt: string;
  subjectImage: string;
  onAction: (newImage: { id: string; url: string }) => void;
  imageCategories: string[];
  setImageCategories: (categories: string[]) => void;

  // pushToHistory: (url: string) => void;
  setIsGenerating: (val: boolean) => void;
  canvasWidth?: string;
}) => {
  const [tab, setTab] = useState<'prompt' | 'image'>('prompt');
  const [backgroundPrompt, setBackgroundPrompt] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const _handleReplace = () => {
    if (tab === 'prompt') {
      replaceUsingPrompt();
    } else {
      replaceUsingImage();
    }
  };
  void _handleReplace;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setBackgroundImage(file);
    }
  };

  const toBase64 = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const replaceUsingPrompt = async () => {
    if (tab === 'prompt' && !backgroundPrompt) {
      showSnackbar('Please enter a prompt to continue.', 'error');
      return;
    }
    try {
      setIsGenerating(true);
      await canvasApi.executeImageGeneration(
        async request => ({
          ...request,
          subject: id,
          prompt: backgroundPrompt,
          imageCategories: imageCategories
        }),

        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.replaceSubjectBackground(request);
            const newImage = data?.images?.[0];
            if (newImage?.id && newImage?.url) {
              onAction(newImage);
              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while replacing the background.';

            showSnackbar(errorMessage, 'error');
          }
        }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong while replacing the background.';

      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const replaceUsingImage = async () => {
    if (tab === 'image' && !backgroundImage) {
      showSnackbar('Please upload a background image to continue.', 'error');
      return;
    }
    try {
      setIsGenerating(true);
      const referenceImage = await toBase64(backgroundImage);

      await canvasApi.executeImageGeneration(
        async request => ({
          ...request,
          subject: id ? id : subjectImage,
          prompt: initialPrompt ? initialPrompt : 'replace',
          reference_image: referenceImage,
          imageCategories: imageCategories
        }),
        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.replaceSubjectBackground(request);
            const newImage = data?.images?.[0]; // Get the whole object
            if (newImage?.id && newImage?.url) {
              onAction(newImage);
              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while replacing the background.';

            showSnackbar(errorMessage, 'error');
          } finally {
            setIsGenerating(false);
          }
        }
      );

      setBackgroundImage(null);
      setBackgroundPrompt('');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0]?.message || error?.message || 'Something went wrong while replacing the background.';
      showSnackbar(errorMessage, 'error');
    }
  };

  const { showSnackbar } = useSnackbar();

  return (
    <Card
      sx={{
        backgroundColor: '#18181B',
        pt: 2,
        pr: 2,
        pl: 2,
        pb: 1,
        width: canvasWidth==="true" ? '286px' :'320px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRadius: 2,
        height: 'max-content',
        mt: '43px',
        maxHeight: '80vh',
        mx: 'auto'
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="#E5E5E5" fontFamily={'Inter'} fontSize="16px" fontWeight={600}>
          Replace Background
        </Typography>
        <Tooltip
          title={
            <Typography fontSize="10px" color="black" width="200px" fontFamily={'Inter'}>
              Use prompt to describe the new background or upload new background image then click the Replace button.
            </Typography>
          }
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                boxShadow: 1,
                p: 0.5
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: grey[400], cursor: 'pointer' }} />
        </Tooltip>
      </Box>

      <ToggleButtonGroup
        value={tab}
        exclusive
        onChange={(_, val) => val && setTab(val)}
        fullWidth
        sx={{
          backgroundColor: grey[800],
          borderRadius: 2,
          p: '2px',
          gap: '3px',
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            flex: 1,
            fontSize: '10px',
            color: '#C0C0C0',
            border: 'none',
            borderRadius: '8px',
            padding: '2px',
            backgroundColor: 'transparent',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            gap: '10px',

            '&.Mui-selected': {
              color: 'rgba(168, 168, 168, 1)',
              background: 'linear-gradient(black, black) padding-box, linear-gradient(163deg, #0CAECF -29.1%, #580F6B 96.45%) border-box',
              border: '1px solid transparent',
              borderRadius: '8px'
            },

            '&:hover': {
              backgroundColor: grey[700]
            }
          },
          '& .MuiToggleButton-root[value="image"]': {
            fontSize: '12px',
            fontWeight: '400'
          },
          '& .MuiToggleButton-root[value="prompt"]': {
            fontSize: '12px',
            fontWeight: '500'
          }
        }}
      >
        <ToggleButton value="prompt">Use Prompt</ToggleButton>
        <ToggleButton value="image">Use BG Image</ToggleButton>
      </ToggleButtonGroup>

      {tab === 'prompt' ? (
        canvasWidth==="true" ?
          <TextField
            placeholder="Type a prompt to describe new background"
            multiline
            minRows={5}
            fullWidth
            value={backgroundPrompt}
            onChange={(e) => setBackgroundPrompt(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#A8A8A8',
                borderRadius: 2,
                width: '100%',
                height: '124px',
                fontSize: '12px',
                backgroundColor: '#18181B',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#555' },
                '&.Mui-focused fieldset': { borderColor: '#333' },
                alignItems: 'flex-start',
                fontWeight: 400,
              },
              '& .MuiInputBase-input': {
                color: '#A8A8A8',
                fontSize: '12px',
                lineHeight: '18px',
                textAlign: 'left',
                resize: 'none',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#A8A8A8',
                opacity: 1,
                whiteSpace: 'normal',  // allows wrapping
                overflow: 'visible',
                textOverflow: 'unset',
              },
            }}
          />:<TextField placeholder="Type a prompt to describe new background" multiline minRows={5} fullWidth value={backgroundPrompt} onChange={e => setBackgroundPrompt(e.target.value)} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { color: '#A8A8A8', borderRadius: 2, width: '100%', height: '124px', fontSize: '12px', backgroundColor: '#18181B', '& fieldset': { borderColor: '#333' }, '&:hover fieldset': { borderColor: '#555' }, '&.Mui-focused fieldset': { borderColor: '#333' }, alignItems: 'flex-start', padding: '8px', fontWeight: 400, paddingRight: '4px' },  '& .MuiInputBase-input::placeholder': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }} />


      ) : (
        <Card
          sx={{
            backgroundColor: '#18181B',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            // height: '260px',
            padding: '12px',
            gap: 2,
            border: '1px solid #3C3B3B'
          }}
        >
          {backgroundImage ? (
            <>
              <Box
                component="img"
                src={URL.createObjectURL(backgroundImage)}
                alt="Uploaded Background"
                sx={{
                  maxHeight: 180,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: 1
                }}
              />
              {/* <Button
                onClick={handleUploadClick}
                variant="outlined"
                sx={{
                  borderColor: grey[600],
                  color: 'white',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    borderColor: grey[400],
                    backgroundColor: grey[800]
                  }
                }}
              >
                Replace Image
              </Button> */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  src={Deleteicon}
                  alt="Replace"
                  onClick={() => setBackgroundImage(null)}
                  sx={{
                    width: 36,
                    height: 38,
                    cursor: 'pointer',
                    opacity: 1,
                    transition: 'opacity 0.3s'
                  }}
                />
                <Box></Box>
              </Box>
            </>
          ) : (
            <>
              <img src={CloudUploadIcon} alt="Upload" />

              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  verticalAlign: 'middle',
                  whiteSpace: 'nowrap',
                  opacity: 1,
                  color: 'rgba(168, 168, 168, 1)'
                }}
              >
                Click to upload new background image
              </Typography>

              <Button
                onClick={handleUploadClick}
                variant="outlined"
                sx={{
                  borderColor: grey[600],
                  color: '#A8A8A8',
                  textTransform: 'capitalize', // from your typography
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontStyle: 'normal', // 'Regular' becomes 'normal'
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  verticalAlign: 'middle',
                  width: '98px',
                  height: '28px',
                  maxHeight: '52px',
                  opacity: 1,
                  gap: '10px',
                  paddingTop: '12.5px',
                  paddingRight: '12px',
                  paddingBottom: '12.5px',
                  paddingLeft: '12px',
                  borderRadius: '170px',
                  borderWidth: '1px',
                  '&:hover': {
                    borderColor: grey[400],
                    backgroundColor: grey[800]
                  }
                }}
              >
                Choose File
              </Button>
            </>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        </Card>
      )}

      {/* <Button
        // onClick={_handleReplace}
        sx={{
          maxWidth: '200px',
          maxHeight: '52px',
          fontSize: '12px',
          padding: '8px 30px',
          width: '104px',
          height: '29px',
          borderRadius: '170px',
          fontWeight: '600',
          background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
          color: 'white',
          textTransform: 'none',
          marginLeft: 'auto'
        }}
        onClick={tab == 'prompt' ? replaceUsingPrompt : replaceUsingImage}
      >
        Replace
      </Button> */}

      <Button
        // onClick={_handleReplace}
        sx={{
          maxWidth: '87px',
          maxHeight: '52px',
          fontSize: '12px',
          padding: '16px 40px',
          width: '87px',
          height: '29px',
          borderRadius: '170px',
          fontWeight: '600',
          background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
          color: 'white',
          textTransform: 'none',
          marginLeft: 'auto',
          gap: '10px'
        }}
        onClick={tab == 'prompt' ? replaceUsingPrompt : replaceUsingImage}
      >
        Replace
      </Button>
    </Card>
  );
};

export default ReplaceBG;
